import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { dateTimeFormatter } from "_helper/timeFormatter";
import {
    useTransportResourceServiceV1GetVendorDetailQuery,
    TransportResourceVendorAddressDetail,
} from "_services/modules/transport-resource/vendor";
import { useFleetServiceV1GetFleetsQuery } from "_services/modules/transport-resource/fleet";
import { TableSortState } from "components/table";

export interface FlattenedAddress extends TransportResourceVendorAddressDetail {
    branch_name: string;
    country_name?: string;
    country?: string;
    province?: string;
    city?: string;
    state_name?: string;
}

export interface FleetRow {
    id: string;
    plateNumber: string;
    vehicleType: string;
    vehicleModel: string;
    odometer: string;
    status: string;
    stnkExpiry: string;
    kirExpiry: string;
    lastMaintenance: string;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
}

const useVendorDetail = () => {
    const dispatch = useAppDispatch();
    const { t } = useAppTranslation("vendor");
    const { id = "" } = useParams();

    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<"fleet-list" | "branch-list" | "address-list">(
        "fleet-list",
    );

    // Fleet list server-side pagination and sort states
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortState, setSortState] = useState<TableSortState>(null);

    // Branch list client-side pagination states
    const [branchPage, setBranchPage] = useState(1);
    const [branchRowsPerPage, setBranchRowsPerPage] = useState(5);

    // Address list client-side pagination states
    const [addressPage, setAddressPage] = useState(1);
    const [addressRowsPerPage, setAddressRowsPerPage] = useState(5);

    const { data: detail, isLoading: isDetailLoading } = useTransportResourceServiceV1GetVendorDetailQuery({ id }, {
        skip: !id,
    });

    const { data: fleetData, isLoading: isFleetLoading, isFetching: isFleetFetching } = useFleetServiceV1GetFleetsQuery(
        {
            vendorId: id,
            page: currentPage,
            limit: rowsPerPage,
            sortBy: sortState?.columnId,
            order: sortState?.direction,
        },
        {
            skip: !id || activeTab !== "fleet-list",
        }
    );

    useEffect(() => {
        // @ts-expect-error temporary i18n key typing limitation
        const val = t("detail-page-title");
        const resolvedTitle = val === "detail-page-title" ? "Vendor Detail" : val;
        dispatch(
            setGlobalComponent({
                title: resolvedTitle,
                hasBackButton: true,
            }),
        );
    }, [dispatch, t]);

    const flattenedAddresses = useMemo(() => {
        if (!detail?.branches) return [];
        const result: FlattenedAddress[] = [];
        detail.branches.forEach((branch) => {
            if (branch.addresses) {
                branch.addresses.forEach((address) => {
                    result.push({
                        ...address,
                        branch_name: branch.branch_name ?? "-",
                    });
                });
            }
        });
        return result;
    }, [detail]);

    const fleetRows = useMemo(() => {
        if (!fleetData?.fleet) return [];
        return fleetData.fleet.map((item) => ({
            id: item.id || "",
            plateNumber: item.plate_number || "-",
            vehicleType: item.vehicle_type || "-",
            vehicleModel: item.vehicle_model || "-",
            odometer: item.odometer != null ? `${item.odometer.toLocaleString()} Km` : "-",
            status: item.status || "-",
            stnkExpiry: item.stnk_expiry || "-",
            kirExpiry: item.kir_expiry || "-",
            lastMaintenance: item.last_maintenance_date || "-",
            createdBy: item.created_by || "-",
            createdAt: item.created_at ? dateTimeFormatter(item.created_at) : "-",
            updatedBy: item.updated_by || "-",
            updatedAt: item.updated_at ? dateTimeFormatter(item.updated_at) : "-",
        }));
    }, [fleetData]);

    // Client-side branch pagination
    const totalBranchPages = Math.ceil((detail?.branches?.length || 0) / branchRowsPerPage);
    const paginatedBranches = useMemo(() => {
        if (!detail?.branches) return [];
        const startIndex = (branchPage - 1) * branchRowsPerPage;
        return detail.branches.slice(startIndex, startIndex + branchRowsPerPage);
    }, [detail?.branches, branchPage, branchRowsPerPage]);

    // Client-side address pagination
    const totalAddressPages = Math.ceil(flattenedAddresses.length / addressRowsPerPage);
    const paginatedAddresses = useMemo(() => {
        const startIndex = (addressPage - 1) * addressRowsPerPage;
        return flattenedAddresses.slice(startIndex, startIndex + addressRowsPerPage);
    }, [flattenedAddresses, addressPage, addressRowsPerPage]);

    return {
        id,
        detail,
        flattenedAddresses,
        fleetRows,
        fleetTotalPages: fleetData?.metadata?.total_page || 0,
        isHistoryOpen,
        setIsHistoryOpen,
        activeTab,
        setActiveTab,
        
        // Fleet pagination
        currentPage,
        setCurrentPage,
        rowsPerPage,
        setRowsPerPage,
        sortState,
        setSortState,
        
        // Branch pagination
        branchPage,
        setBranchPage,
        branchRowsPerPage,
        setBranchRowsPerPage,
        totalBranchPages,
        paginatedBranches,
        
        // Address pagination
        addressPage,
        setAddressPage,
        addressRowsPerPage,
        setAddressRowsPerPage,
        totalAddressPages,
        paginatedAddresses,
        
        isLoading: isDetailLoading,
        isFleetLoading: isFleetLoading || isFleetFetching,
        t,
    };
};

export default useVendorDetail;
